import authStorage from '../utils/authStorage';
const BASE_URL = '/api';

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token, null));
  refreshSubscribers = [];
};

const onRefreshFailed = (err) => {
  refreshSubscribers.forEach((cb) => cb(null, err));
  refreshSubscribers = [];
};

const requestCache = new Map();

export const clearApiCache = () => {
  requestCache.clear();
};

const executeRequest = async (endpoint, options = {}) => {
  let token = authStorage.getItem('token');

  // We need to omit Content-Type for FormData so the browser can automatically set the boundary
  const isFormData = options.body instanceof FormData;

  let headers = {
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  let config = {
    ...options,
    headers,
  };

  try {
    let response = await fetch(`${BASE_URL}${endpoint}`, config);

    // Check for expired token or unauthorized response status
    if (response.status === 401 && endpoint !== '/auth/login' && endpoint !== '/auth/refresh') {
      const refreshToken = authStorage.getItem('refreshToken');
      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const refreshResponse = await fetch(`${BASE_URL}/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken })
            });

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              if (refreshData.accessToken) {
                authStorage.setItem('token', refreshData.accessToken);
                isRefreshing = false;
                onRefreshed(refreshData.accessToken);

                // Retry the original request that triggered the refresh
                const retryHeaders = {
                  ...headers,
                  'Authorization': `Bearer ${refreshData.accessToken}`
                };
                response = await fetch(`${BASE_URL}${endpoint}`, {
                  ...options,
                  headers: retryHeaders
                });
              } else {
                isRefreshing = false;
                onRefreshFailed(new Error('Session expired. Please log in again.'));
                authStorage.removeItem('token');
                authStorage.removeItem('refreshToken');
                authStorage.removeItem('user');
                window.location.href = '/login';
                return { error: 'Session expired. Please log in again.', success: false };
              }
            } else {
              isRefreshing = false;
              onRefreshFailed(new Error('Session expired. Please log in again.'));
              authStorage.removeItem('token');
              authStorage.removeItem('refreshToken');
              authStorage.removeItem('user');
              window.location.href = '/login';
              return { error: 'Session expired. Please log in again.', success: false };
            }
          } catch (refreshErr) {
            isRefreshing = false;
            onRefreshFailed(refreshErr);
            return { error: 'Connection failed during session refresh.', success: false };
          }
        } else {
          // Wait for token refresh and retry
          const retryPromise = new Promise((resolve, reject) => {
            subscribeTokenRefresh((newToken, err) => {
              if (err) {
                reject(err);
              } else {
                const retryHeaders = {
                  ...headers,
                  'Authorization': `Bearer ${newToken}`
                };
                resolve(fetch(`${BASE_URL}${endpoint}`, {
                  ...options,
                  headers: retryHeaders
                }));
              }
            });
          });

          try {
            response = await retryPromise;
          } catch (err) {
            return { error: err.message || 'Session expired or refresh failed.', success: false };
          }
        }
      } else {
        // No refresh token available, logout and redirect to /login immediately
        authStorage.removeItem('token');
        authStorage.removeItem('refreshToken');
        authStorage.removeItem('user');
        window.location.href = '/login';
        return { error: 'Session expired. Please log in again.', success: false };
      }
    }

    // Get text first to prevent JSON parse errors
    const text = await response.text();
    let data;
    let isJson = true;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = { message: text || 'Server Error' };
      isJson = false;
    }

    if (!response.ok || !isJson) {
      console.error(`API Error (${response.status}):`, data);
      let errorMessage = (typeof data.detail === 'string' ? data.detail : null) || data.message || data.error || `Error ${response.status}: ${response.statusText}`;
      if (!isJson) {
        errorMessage = "Invalid JSON response from server";
      }
      return { error: String(errorMessage), success: false, data };
    }

    return { data, success: true };
  } catch (err) {
    console.error('Fetch error:', err);
    return {
      error: 'Network error: Connection failed. Please check if the Gateway (8080) is running and CORS is allowed.',
      success: false
    };
  }
};

export const request = async (endpoint, options = {}) => {
  const isGet = !options.method || options.method.toUpperCase() === 'GET';
  
  if (!isGet) {
    clearApiCache();
    return executeRequest(endpoint, options);
  }

  const cacheKey = endpoint;

  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const result = await executeRequest(endpoint, options);
      if (!result.success) {
        requestCache.delete(cacheKey);
      } else {
        setTimeout(() => {
          requestCache.delete(cacheKey);
        }, 5000);
      }
      return result;
    } catch (err) {
      requestCache.delete(cacheKey);
      throw err;
    }
  })();

  requestCache.set(cacheKey, promise);
  return promise;
};
